import React, { useState, useEffect } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useLocation } from 'react-router-dom';
import { toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';

// Declare window interfaces for tracking
declare global {
  interface Window {
    fbq?: (
      command: 'track' | 'trackCustom',
      eventName: string,
      parameters?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

const SMS_NUMBER = '5082031342';
const DISPLAY_NUMBER = '508-203-1342';
const GOOGLE_MAPS_LINK = 'https://www.google.com/maps/search/?api=1&query=Atomic+Tattoo+Removal+78+South+St+201+Wrentham+MA+02093';
const DEFAULT_TIMEZONE = 'America/New_York';

// ──────────────────────────────────────────────────────────────
// PHONE MODAL
// ──────────────────────────────────────────────────────────────

const PhoneModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#fff', borderRadius: 16, padding: '32px 40px',
        textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
        maxWidth: 340, width: '90%',
      }}
    >
      <p style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5C96AC', marginBottom: 8 }}>
        Text Us
      </p>
      <p style={{ fontSize: 'clamp(1.6rem, 6vw, 2.2rem)', fontWeight: 800, color: '#1e3a5f', marginBottom: 8 }}>
        {DISPLAY_NUMBER}
      </p>
      <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 20 }}>
        Have questions? Text us anytime and we'll help you prepare for your consultation.
      </p>
      <button
        onClick={onClose}
        style={{
          background: '#cb4f2e', color: '#fff', border: 'none', borderRadius: 50,
          padding: '10px 28px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
        }}
      >
        Close
      </button>
    </div>
  </div>
);

const BookingThankYou: React.FC = () => {
  const location = useLocation();
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Extract state from navigation
  const { name, dateTime } = location.state || {};
  
  // Format the date and time
  const formatAppointmentDateTime = () => {
    if (!dateTime) return null;
    
    try {
      const appointmentDate = new Date(dateTime);
      const zonedDate = toZonedTime(appointmentDate, DEFAULT_TIMEZONE);
      
      return {
        fullDate: format(zonedDate, 'EEEE, MMMM d, yyyy'),
        time: format(zonedDate, 'h:mm a'),
        dayOfWeek: format(zonedDate, 'EEEE'),
      };
    } catch (err) {
      console.error('Error formatting date:', err);
      return null;
    }
  };
  
  const formattedDateTime = formatAppointmentDateTime();
  const firstName = name ? name.split(' ')[0] : '';

  // Detect mobile device
  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Track appointment booking event
  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'appointment_booked',
        event_category: 'booking',
        event_label: 'Consultation Booked',
      });
    }
  }, []);

  // Handle SMS link click
  const handleSmsClick = () => {
    if (window.fbq) {
      window.fbq('track', 'Contact');
    }
  };

  return (
    <>
      {showPhoneModal && <PhoneModal onClose={() => setShowPhoneModal(false)} />}
      <Helmet>
        <title>Consultation Booked | Atomic Tattoo Removal</title>
        <meta
          name="description"
          content="Your free laser tattoo removal consultation is confirmed. We look forward to meeting you at Atomic Tattoo Removal in Wrentham, MA."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div
        className="booking-thank-you-page"
        style={{
          maxWidth: 680,
          margin: '0 auto',
          padding: '24px 16px 48px',
        }}
      >
        {/* Success Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #2d8659 0%, #3ba36e 100%)',
            borderRadius: 20,
            padding: '40px 24px',
            textAlign: 'center',
            marginBottom: 32,
            boxShadow: '0 6px 24px rgba(45,134,89,0.20)',
          }}
        >
          <div
            style={{
              fontSize: '4rem',
              marginBottom: 16,
              lineHeight: 1,
            }}
          >
            ✓
          </div>
          <h1
            style={{
              fontSize: 'clamp(1.6rem, 5vw, 2.2rem)',
              fontWeight: 800,
              color: '#fff',
              marginBottom: 12,
              lineHeight: 1.2,
            }}
          >
            You're All Set{firstName && `, ${firstName}`}!
          </h1>
          <p
            style={{
              color: '#d0f5e0',
              fontSize: 'clamp(1rem, 3vw, 1.15rem)',
              fontWeight: 600,
              maxWidth: 480,
              margin: '0 auto 8px',
              lineHeight: 1.6,
            }}
          >
            Your free tattoo removal consultation is confirmed.
          </p>
          
          {formattedDateTime && (
            <div
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: '2px solid rgba(255,255,255,0.25)',
                borderRadius: 12,
                padding: '20px',
                marginTop: 24,
                maxWidth: 400,
                margin: '24px auto 0',
              }}
            >
              <p
                style={{
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 12,
                }}
              >
                Your Appointment
              </p>
              <p
                style={{
                  color: '#fff',
                  fontSize: 'clamp(1.15rem, 3vw, 1.35rem)',
                  fontWeight: 700,
                  marginBottom: 4,
                  lineHeight: 1.3,
                }}
              >
                {formattedDateTime.dayOfWeek}
              </p>
              <p
                style={{
                  color: '#d0f5e0',
                  fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                  lineHeight: 1.5,
                  marginBottom: 0,
                }}
              >
                {formattedDateTime.fullDate}<br />
                {formattedDateTime.time}
              </p>
            </div>
          )}
        </div>

        {/* What to Expect */}
        <div
          style={{
            background: '#f8fbfd',
            border: '2px solid #c2dae6',
            borderRadius: 16,
            padding: 'clamp(24px, 5vw, 32px)',
            marginBottom: 24,
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(1.3rem, 4vw, 1.65rem)',
              fontWeight: 700,
              color: '#1e3a5f',
              marginBottom: 16,
              textAlign: 'center',
            }}
          >
            What to Expect at Your Free Consultation
          </h2>
          
          <div style={{ color: '#555', fontSize: '1rem', lineHeight: 1.7 }}>
            <p style={{ marginBottom: 16 }}>
              During your consultation, we'll:
            </p>
            
            <ul style={{ paddingLeft: 24, marginBottom: 20 }}>
              <li style={{ marginBottom: 12 }}>
                <strong style={{ color: '#1e3a5f' }}>Explain the tattoo removal process</strong> — how it works, what to expect during treatment, and typical results
              </li>
              <li style={{ marginBottom: 12 }}>
                <strong style={{ color: '#1e3a5f' }}>Discuss your goals</strong> — complete removal or fading for cover-up
              </li>
              <li style={{ marginBottom: 12 }}>
                <strong style={{ color: '#1e3a5f' }}>Answer all your questions</strong> — safety, pain management, aftercare
              </li>
              <li>
                <strong style={{ color: '#1e3a5f' }}>Get Started on Treatment</strong> — We allocate extra time so you can get started same day
              </li>              
            </ul>
            
            <p style={{ marginBottom: 0, fontWeight: 600, color: '#2d5986' }}>
              Your consultation is completely free with no obligation to book treatment.
            </p>
          </div>
        </div>

        {/* Location & Directions */}
        <div
          style={{
            background: '#fff',
            border: '2px solid #dde4ec',
            borderRadius: 16,
            padding: 'clamp(24px, 5vw, 32px)',
            marginBottom: 24,
            textAlign: 'center',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
              fontWeight: 700,
              color: '#1e3a5f',
              marginBottom: 16,
            }}
          >
            📍 Location
          </h2>
          
          <p
            style={{
              color: '#555',
              fontSize: '1.05rem',
              lineHeight: 1.6,
              marginBottom: 20,
            }}
          >
            <strong style={{ color: '#1e3a5f' }}>Atomic Tattoo Removal</strong><br />
            78 South St, #201<br />
            Wrentham, MA 02093
          </p>
          
          <a
            href={GOOGLE_MAPS_LINK}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              background: '#2d5986',
              color: '#fff',
              fontWeight: 700,
              fontSize: '1rem',
              padding: '14px 32px',
              borderRadius: 50,
              textDecoration: 'none',
              boxShadow: '0 4px 16px rgba(45,89,134,0.25)',
              transition: 'transform 0.2s, background 0.2s',
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.background = '#1e3a5f';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.background = '#2d5986';
            }}
          >
            Get Directions
          </a>
        </div>

        {/* Questions CTA */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5986 100%)',
            borderRadius: 16,
            padding: 'clamp(24px, 5vw, 32px)',
            textAlign: 'center',
            marginBottom: 24,
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
              fontWeight: 700,
              color: '#fff',
              marginBottom: 12,
            }}
          >
            Have Questions?
          </h2>
          <p
            style={{
              color: '#d0e8f5',
              fontSize: '1rem',
              maxWidth: 480,
              margin: '0 auto 20px',
              lineHeight: 1.6,
            }}
          >
            Text us anytime if you have questions about your consultation, pricing, or what to expect.
          </p>
          
          {isMobile ? (
            <a
              href={`sms:${SMS_NUMBER}`}
              onClick={handleSmsClick}
              style={{
                display: 'inline-block',
                background: '#cb4f2e',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.05rem',
                padding: '14px 40px',
                borderRadius: 50,
                textDecoration: 'none',
                letterSpacing: '0.02em',
                boxShadow: '0 4px 18px rgba(203,79,46,0.35)',
                transition: 'background 0.2s',
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#a83c22')}
              onMouseOut={e => (e.currentTarget.style.background = '#cb4f2e')}
            >
              Text Us Now
            </a>
          ) : (
            <button
              onClick={() => { handleSmsClick(); setShowPhoneModal(true); }}
              style={{
                display: 'inline-block',
                background: '#cb4f2e',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.05rem',
                padding: '14px 40px',
                borderRadius: 50,
                border: 'none',
                cursor: 'pointer',
                letterSpacing: '0.02em',
                boxShadow: '0 4px 18px rgba(203,79,46,0.35)',
                transition: 'background 0.2s',
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#a83c22')}
              onMouseOut={e => (e.currentTarget.style.background = '#cb4f2e')}
            >
              Text Us Now
            </button>
          )}
          
          <p
            style={{
              color: '#90c8e0',
              fontSize: '0.95rem',
              marginTop: 12,
              marginBottom: 0,
            }}
          >
            {DISPLAY_NUMBER}
          </p>
        </div>

        {/* Email Confirmation Note */}
        <div
          style={{
            textAlign: 'center',
            color: '#777',
            fontSize: '0.9rem',
            lineHeight: 1.6,
          }}
        >
          <p style={{ marginBottom: 0 }}>
            📧 <strong>Check your email</strong> for your appointment confirmation and calendar invite.
          </p>
        </div>
      </div>
    </>
  );
};

export default BookingThankYou;
